import { useEffect } from "react";
import { Navigate, useParams } from "react-router-dom";

interface DeepLinkRedirectProps {
  /** Target path with `:slug` placeholder, e.g. `/insights?article=:slug` or `/activities?tab=archive#:slug` */
  to: string;
  /** Static fallback when the route has no slug param. */
  fallback?: string;
}

/**
 * Renders nothing visually. Reads `:slug` from the URL and immediately
 * redirects (replace) to the canonical query-string form so the existing
 * page-level deep-linking logic runs untouched.
 *
 * Pretty paths exist purely so crawlers can index per-article HTML shells
 * (built by the prerender plugin) without duplicate-content concerns from
 * `?article=` query URLs.
 */
const DeepLinkRedirect = ({ to, fallback = "/" }: DeepLinkRedirectProps) => {
  const params = useParams<{ slug?: string }>();
  const slug = params.slug;

  useEffect(() => {
    // No-op — Navigate below handles the redirect on first render.
  }, []);

  if (!slug) return <Navigate to={fallback} replace />;
  const target = to.replace(":slug", slug);
  return <Navigate to={target} replace />;
};

export default DeepLinkRedirect;
